/**
 * Read API for the Revenue Attribution report.
 *
 * Two GETs back the whole tab: /summary for the stat cards, /trend for the
 * channel chart AND the AI sources bar. The sources bar is a field on the trend
 * response, not an endpoint — fetching /trend twice doubles ClickHouse load for
 * identical data.
 *
 * Result shape and auth mirror gscApi.ts, which fronts the same service.
 */
import Cookies from "js-cookie";

import { API_ENDPOINTS } from "constants/http-endpoints";

const BASE = API_ENDPOINTS.AI_VISIBILITY;

export const ATTRIBUTION_ROUTES = {
    summary: "/revenue-attribution/summary",
    trend: "/revenue-attribution/trend",
    pages: "/revenue-attribution/pages"
} as const;

export type AttributionResult<T> = { ok: true; data: T } | { ok: false; message: string; badRequest: boolean };

// ── Shared params ────────────────────────────────────────────────────────────

export type AttributionScope = "session" | "firsttouch";
export type TrendMetricParam = "sessions" | "conversions" | "revenue";
export type TrendGranularity = "day" | "week";

export interface AttributionWindow {
    /** Plain YYYY-MM-DD. Never construct a Date from these — see formatApiDate. */
    startDate?: string;
    endDate?: string;
    attributionScope?: AttributionScope;
}

// ── Responses ────────────────────────────────────────────────────────────────

/** null is meaningful throughout: it means "no claim", not zero. */
export interface AttributionMetric {
    value: number | null;
    delta_pct: number | null;
}

export interface AttributionRevenue extends AttributionMetric {
    currency: string;
}

export interface AttributionRevenuePerSession {
    ai: number | null;
    organic: number | null;
    /** null when the organic baseline was zero. */
    multiple: number | null;
}

export interface AttributionSummaryMeta {
    configured: boolean;
    no_data: boolean;
    low_sample: boolean;
    low_sample_threshold: number;
    /** Last complete day of data, YYYY-MM-DD. */
    data_through: string;
    /** True only when GA4 is more than 48h behind; one day of lag isn't flagged. */
    data_stale: boolean;
    has_ga4_revenue: boolean;
    derived_revenue: boolean;
    unattributed_revenue: number | null;
    unattributed_conversions: number | null;
    total_revenue: number | null;
    total_conversions: number | null;
    /** Organic baseline still contains branded search, so the multiple is understated. */
    organic_includes_branded_search: boolean;
    attribution_scope: AttributionScope;
    currency: string;
    time_zone: string;
    property_id: string;
    warnings: string[];
}

export interface AttributionSummary {
    period: { start: string; end: string };
    ai_sessions: AttributionMetric;
    ai_conversions: AttributionMetric;
    ai_revenue: AttributionRevenue;
    revenue_per_session: AttributionRevenuePerSession;
    meta: AttributionSummaryMeta;
}

export type TrendChannel = "ai_search" | "direct" | "organic";

export interface TrendSeriesPoint {
    /** Bucket start date, already in the property's timezone. */
    date: string;
    value: number;
}

export interface TrendSeries {
    channel: TrendChannel;
    label: string;
    points: TrendSeriesPoint[];
}

export interface AiSource {
    vendor: string;
    /** Display name is supplied — don't title-case `vendor` yourself. */
    label: string;
    sessions: number;
    share_pct: number;
}

export interface AttributionTrendMeta {
    configured: boolean;
    no_data: boolean;
    /** Traffic outside the three plotted channels — why the series don't sum to the total. */
    excluded_sessions: number | null;
    unattributed_conversions: number | null;
    unattributed_revenue: number | null;
    derived_revenue: boolean;
    data_through: string;
    data_stale: boolean;
    week_starts_on: string;
    attribution_scope: AttributionScope;
    currency: string;
    time_zone: string;
    warnings: string[];
}

export interface AttributionTrend {
    period: { start: string; end: string };
    metric: TrendMetricParam;
    granularity: TrendGranularity;
    /** Exactly three, always present, already zero-filled across every bucket. */
    series: TrendSeries[];
    ai_sources: AiSource[];
    meta: AttributionTrendMeta;
}

// ── Transport ────────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = (Cookies.get("system_token") ?? "").trim();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

function query(params: Record<string, string | undefined>): string {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v) qs.set(k, v);
    });
    const s = qs.toString();
    return s ? `?${s}` : "";
}

/**
 * Every degraded state arrives as a 200 with a typed body — not-configured,
 * no-data, stale and low-sample are all normal responses. Only a 400 is a real
 * failure, and it means a malformed param, so it surfaces as a bug.
 */
async function getJson<T>(path: string, fallbackError: string): Promise<AttributionResult<T>> {
    try {
        const res = await fetch(`${BASE}${path}`, { headers: authHeaders() });
        if (!res.ok) {
            let message = fallbackError;
            try {
                const body = await res.json();
                message = body?.message || body?.error || fallbackError;
            } catch {
                /* non-JSON error body — keep the fallback */
            }
            return { ok: false, message, badRequest: res.status === 400 };
        }
        return { ok: true, data: (await res.json()) as T };
    } catch {
        return {
            ok: false,
            message: "Network error. Please check your connection and try again.",
            badRequest: false
        };
    }
}

export function fetchAttributionSummary(opts: AttributionWindow = {}) {
    const path = `${ATTRIBUTION_ROUTES.summary}${query({
        start_date: opts.startDate,
        end_date: opts.endDate,
        attribution_scope: opts.attributionScope
    })}`;
    return getJson<AttributionSummary>(path, "Could not load the attribution summary.");
}

export function fetchAttributionTrend(
    opts: AttributionWindow & { metric?: TrendMetricParam; granularity?: TrendGranularity } = {}
) {
    const path = `${ATTRIBUTION_ROUTES.trend}${query({
        start_date: opts.startDate,
        end_date: opts.endDate,
        metric: opts.metric,
        granularity: opts.granularity,
        attribution_scope: opts.attributionScope
    })}`;
    return getJson<AttributionTrend>(path, "Could not load the attribution trend.");
}

// ── Page performance ─────────────────────────────────────────────────────────

export type PageSortKey = "revenue" | "sessions" | "conversions" | "cvr" | "value_per_session" | "citations";
export type SortDir = "asc" | "desc";
export type PageTypeFilter = "comparison" | "pricing" | "integration" | "content" | "other";

/** How the row's revenue figure was arrived at. */
export type RevenueSource = "ga4_native" | "modeled" | "none";

export interface PageVendor {
    vendor: string;
    label: string;
    sessions: number;
}

export interface CitedPrompt {
    id: string;
    text: string;
    vendor: string;
    position: number;
}

export interface PageCitations {
    /** The true total. `prompts` is capped at 10, so never use its length. */
    count: number;
    prompts: CitedPrompt[];
    truncated: boolean;
}

export interface AttributionPageRow {
    page: string;
    page_type: string;
    ai_sessions: number;
    conversions: number;
    /** AI-only conversion rate — this is what the CVR column shows. */
    cvr: number | null;
    /** Across all traffic; only referenced by the $/session explanation. */
    cvr_all_traffic: number | null;
    value_per_session: number | null;
    revenue: number | null;
    revenue_source: RevenueSource;
    low_sample: boolean;
    vendors: PageVendor[];
    /** null means the prompt store was unreachable — not "no citations". */
    citations: PageCitations | null;
}

export interface AttributionPagination {
    page: number;
    page_size: number;
    /** The whole filtered set, not the current page. */
    total_rows: number;
}

export interface AttributionPageTotals {
    ai_sessions: number | null;
    conversions: number | null;
    revenue: number | null;
}

export interface AttributionPagesMeta {
    configured: boolean;
    no_ai_traffic: boolean;
    /** False → money columns are null and only volume columns can be sorted. */
    revenue_configured: boolean;
    citations_available: boolean;
    low_sample_threshold: number;
    /** GA4 collapsed pages into "(other)", so the list is incomplete. */
    other_bucket_present: boolean;
    unattributed_conversions: number | null;
    pages_excluded_unattributed: number | null;
    data_through: string;
    currency: string;
    time_zone: string;
    warnings: string[];
}

export interface AttributionPages {
    period: { start: string; end: string };
    rows: AttributionPageRow[];
    pagination: AttributionPagination;
    totals: AttributionPageTotals;
    currency: string;
    meta: AttributionPagesMeta;
}

export interface AttributionPagesQuery extends AttributionWindow {
    sortBy?: PageSortKey;
    sortDir?: SortDir;
    page?: number;
    pageSize?: number;
    pageType?: PageTypeFilter;
    vendor?: string;
}

/** Sorting and pagination are server-side — never sort or slice `rows` locally. */
export function fetchAttributionPages(opts: AttributionPagesQuery = {}) {
    const path = `${ATTRIBUTION_ROUTES.pages}${query({
        start_date: opts.startDate,
        end_date: opts.endDate,
        sort_by: opts.sortBy,
        sort_dir: opts.sortDir,
        page: opts.page ? String(opts.page) : undefined,
        page_size: opts.pageSize ? String(opts.pageSize) : undefined,
        page_type: opts.pageType,
        vendor: opts.vendor,
        attribution_scope: opts.attributionScope
    })}`;
    return getJson<AttributionPages>(path, "Could not load page performance.");
}

/** Percentages arrive as rates; null stays null rather than becoming 0%. */
export function formatRate(rate: number | null, decimals = 2): string {
    return rate === null ? EM_DASH : `${(rate * 100).toFixed(decimals)}%`;
}

// ── Date + number helpers ────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Formats a YYYY-MM-DD returned by the API.
 *
 * Deliberately does NOT use `new Date(str)`: that parses as UTC midnight, so
 * anyone west of UTC sees the previous day. These are calendar days already
 * resolved in the GA4 property's timezone, so the string is split directly.
 */
export function formatApiDate(date: string): string {
    const [, m, d] = date.split("-");
    const month = MONTHS[Number(m) - 1];
    return month ? `${month} ${Number(d)}` : date;
}

export function formatApiDateLong(date: string): string {
    const [y, m, d] = date.split("-");
    const month = MONTHS[Number(m) - 1];
    return month ? `${Number(d)} ${month} ${y}` : date;
}

/** Builds a YYYY-MM-DD without going through Date parsing of an API string. */
export function toApiDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Em dash for every null — a rendered 0 is a claim the API declined to make. */
export const EM_DASH = "—";

export function formatCount(value: number | null): string {
    return value === null ? EM_DASH : Math.round(value).toLocaleString("en-US");
}

export function formatDeltaPct(delta: number | null): string | null {
    return delta === null ? null : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta).toFixed(0)}%`;
}

/**
 * Currency comes from the GA4 property and can be empty — a hardcoded $ would
 * silently mislabel an INR tenant, so an empty code renders the bare number.
 */
export function formatMoney(value: number | null, currency: string, decimals = 0): string {
    if (value === null) return EM_DASH;
    const n = value.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
    if (!currency) return n;
    try {
        return value.toLocaleString("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    } catch {
        // Unknown code — show it alongside rather than guessing a symbol.
        return `${n} ${currency}`;
    }
}
